/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import { aviMediumDateFormatWithSec } from 'ng/modules/core/pipes/avi-date.pipe';

import { ILogsStates, LogsStore } from '../../../services/logs.store';
import { ILogCinematicStates, LogCinematicStore } from '../log-cinematic.store';

import * as l10n from './log-cinematic-selection-bar.component.l10n';
import './log-cinematic-selection-bar.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const LEFT_ARROW_KEY = 'ArrowLeft';
const RIGHT_ARROW_KEY = 'ArrowRight';

/**
 * @description
 *     A selection bar for switching backward / forward a log entry in c-view.
 *     The switching is based on existent data from the currently loaded log list, therefore it
 *     normally doesn't require loading except when page turning is needed.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'log-cinematic-selection-bar',
    templateUrl: 'log-cinematic-selection-bar.component.html',
})
export class LogCinematicSelectionBarComponent {
    /**
     * Timestamp of the current log entry.
     */
    @Input()
    public timestamp: number;

    /**
     * True when there is a log entry to switch backward to.
     */
    @Input()
    public hasPrevious: boolean;

    /**
     * True when there is a log entry to switch forward to.
     */
    @Input()
    public hasNext: boolean;

    /**
     * True only when data is not ready.
     * ie. turning the page; jumping to next / previous, when the last / first log entry of the list
     * is hit.
     */
    @Input()
    public busy = true;

    /**
     * Output to react to backward jumping.
     * It should trigger a log entry switching w/wo a -1 page load if available.
     */
    @Output()
    public onPreviousClick = new EventEmitter<void>();

    /**
     * Output to react to backward jumping.
     * It should trigger a log entry switching w/wo a -1 page load if available.
     */
    @Output()
    public onNextClick = new EventEmitter<void>();

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    public readonly vsAppProfileType$ = this.logsStore.vsAppProfileType$;

    /**
     * Format string that specifies date transformation to seconds.
     */
    public readonly timeFormat = aviMediumDateFormatWithSec;

    constructor(
        l10nService: L10nService,
        private readonly logsStore: LogsStore<ILogsStates>,
        public readonly logCinematicStore: LogCinematicStore<ILogCinematicStates>,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Handle left / right arrow key press events and jump to the previous / next log.
     */
    @HostListener('document:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        const { key } = event;

        if (key === LEFT_ARROW_KEY || key === RIGHT_ARROW_KEY) {
            if (!this.busy) {
                event.stopPropagation();

                if (key === LEFT_ARROW_KEY) {
                    this.handlePreviousClick();
                } else {
                    this.handleNextClick();
                }
            }
        }
    }

    /**
     * Handler of clicking the left arrow button. Check if the previous log exists before triggering
     * any effects.
     */
    public handlePreviousClick(): void {
        if (this.hasPrevious) {
            this.onPreviousClick.emit();
        }
    }

    /**
     * Handler of clicking the right arrow button. Check if the next log exists before triggering
     * any effects.
     */
    public handleNextClick(): void {
        if (this.hasNext) {
            this.onNextClick.emit();
        }
    }
}
